import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useJourney, SERVER_LOCATIONS } from '../JourneyContext';
import { Card, Checkbox, Select } from '../../ui';
import api from '../../../api';
import { useTaskTemplates } from '../../../hooks/queries/useTaskTemplates';

// Service card component
const ServiceCard = ({ service, isSelected, onToggle, icon, description }) => (
  <motion.button
    type="button"
    onClick={onToggle}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`
      w-full p-4 rounded-xl border-2 text-left transition-all
      ${isSelected
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
      }
    `}
  >
    <div className="flex items-start gap-3">
      <div className={`
        w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5
        ${isSelected
          ? 'border-blue-500 bg-blue-500'
          : 'border-gray-300 dark:border-gray-600'
        }
      `}>
        {isSelected && (
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <div>
        <div className="font-medium text-gray-900 dark:text-white">{service}</div>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
    </div>
  </motion.button>
);

// Section component
const Section = ({ title, description, children }) => (
  <div className="space-y-4">
    <div>
      <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
      )}
    </div>
    {children}
  </div>
);

export default function OnboardingConfigStep() {
  const { onboarding, updateOnboarding, branches, company, updateCompany } = useJourney();
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Fetch task templates dynamically
  const { data: templates, isLoading: templatesLoading } = useTaskTemplates(company.sectorId);

  // Create dynamic service lists from templates
  const INTERNAL_SYSTEMS = useMemo(() => {
    if (!templates || !Array.isArray(templates)) return [];
    return templates
      .filter((t) => t.group === 'internalSystems' && t.isActive !== false)
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
      .map((t) => t.title);
  }, [templates]);

  const COMPANY_TASKS = useMemo(() => {
    if (!templates || !Array.isArray(templates)) return [];
    return templates
      .filter((t) => t.group === 'companyWide' && t.isActive !== false)
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
      .map((t) => t.title);
  }, [templates]);

  const BRANCH_SERVICES = useMemo(() => {
    if (!templates || !Array.isArray(templates)) return [];
    return templates
      .filter((t) => t.group === 'branchServices' && t.isActive !== false)
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
      .map((t) => t.title);
  }, [templates]);

  // Fetch assignable users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users/assignable');
        setAssignableUsers(response.data);
      } catch (error) {
        console.error('Error fetching assignable users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const toggleSystem = (system) => {
    const systems = onboarding.systems.includes(system)
      ? onboarding.systems.filter(s => s !== system)
      : [...onboarding.systems, system];
    updateOnboarding({ systems });
  };

  const toggleTask = (task) => {
    const companyTasks = onboarding.companyTasks.includes(task)
      ? onboarding.companyTasks.filter(t => t !== task)
      : [...onboarding.companyTasks, task];
    updateOnboarding({ companyTasks });
  };

  const toggleBranchService = (branchIndex, service) => {
    const branchServices = { ...onboarding.branchServices };
    const services = branchServices[branchIndex] || [];
    branchServices[branchIndex] = services.includes(service)
      ? services.filter(s => s !== service)
      : [...services, service];
    updateOnboarding({ branchServices });
  };

  const selectAllBranchServices = (branchIndex) => {
    const branchServices = { ...onboarding.branchServices };
    branchServices[branchIndex] = [...BRANCH_SERVICES];
    updateOnboarding({ branchServices });
  };

  const clearBranchServices = (branchIndex) => {
    const branchServices = { ...onboarding.branchServices };
    branchServices[branchIndex] = [];
    updateOnboarding({ branchServices });
  };

  const toggleTeamMember = (userId) => {
    const technicianIds = company.technicianIds || [];
    const newIds = technicianIds.includes(userId)
      ? technicianIds.filter(id => id !== userId)
      : [...technicianIds, userId];
    updateCompany({ technicianIds: newIds });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin':
        return 'bg-red-500/20 border-red-500/30 text-red-300';
      case 'Manager':
        return 'bg-blue-500/20 border-blue-500/30 text-blue-300';
      case 'Technician':
        return 'bg-green-500/20 border-green-500/30 text-green-300';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-300';
    }
  };

  const serverLocationOptions = [
    { value: '', label: 'Select location...' },
    ...SERVER_LOCATIONS.map(loc => ({ value: loc, label: loc })),
  ];

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Onboarding Configuration
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Select the systems and services to set up during onboarding.
          </p>
        </div>

        <div className="space-y-8">
          {/* Internal Systems */}
          <Section
            title="Internal Systems"
            description="Select which internal systems to configure for this company."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {INTERNAL_SYSTEMS.map(system => (
                <ServiceCard
                  key={system}
                  service={system}
                  isSelected={onboarding.systems.includes(system)}
                  onToggle={() => toggleSystem(system)}
                />
              ))}
            </div>
          </Section>

          {/* Company Tasks */}
          <Section
            title="Company-wide Tasks"
            description="Select tasks that apply to the entire company."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COMPANY_TASKS.map(task => (
                <ServiceCard
                  key={task}
                  service={task}
                  isSelected={onboarding.companyTasks.includes(task)}
                  onToggle={() => toggleTask(task)}
                />
              ))}
            </div>
            
            {/* Onboarding dates */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Onboarding Start Date
                </label>
                <input
                  type="date"
                  value={onboarding.onboardingStartDate}
                  onChange={(e) => updateOnboarding({ onboardingStartDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Onboarding End Date
                </label>
                <input
                  type="date"
                  value={onboarding.onboardingEndDate}
                  onChange={(e) => updateOnboarding({ onboardingEndDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </Section>

          {/* Server Information */}
          <Section
            title="Server Information"
            description="Does the company have servers that need to be managed?"
          >
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg space-y-4">
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasServers"
                    checked={onboarding.hasServers}
                    onChange={() => updateOnboarding({ hasServers: true })}
                    className="w-4 h-4 text-blue-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasServers"
                    checked={!onboarding.hasServers}
                    onChange={() => updateOnboarding({ hasServers: false, serverLocation: '' })}
                    className="w-4 h-4 text-blue-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">No</span>
                </label>
              </div>
              
              {onboarding.hasServers && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Select
                    label="Server Location"
                    options={serverLocationOptions}
                    value={onboarding.serverLocation}
                    onChange={(e) => updateOnboarding({ serverLocation: e.target.value })}
                  />
                </motion.div>
              )}
            </div>
          </Section>

          {/* Branch Services */}
          {branches.length > 0 && (
            <Section
              title="Branch Services"
              description="Select services to set up at each branch."
            >
              <div className="space-y-6">
                {branches.map((branch, index) => (
                  <div key={branch.id || index} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {branch.name || `Branch ${index + 1}`}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {branch.numberOfUsers || 0} users, {branch.numberOfDevices || 0} devices
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => selectAllBranchServices(index)}
                          className="text-xs text-blue-500 hover:text-blue-600"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={() => clearBranchServices(index)}
                          className="text-xs text-gray-500 hover:text-gray-600"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {BRANCH_SERVICES.map(service => (
                        <Checkbox
                          key={service}
                          label={service}
                          checked={(onboarding.branchServices[index] || []).includes(service)}
                          onChange={() => toggleBranchService(index, service)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Team Assignment */}
          <Section
            title="👥 Team Assignment"
            description="Assign team members who will handle this company's onboarding."
          >
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
              </div>
            ) : assignableUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No team members available for assignment.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {assignableUsers.map(user => {
                    const isSelected = (company.technicianIds || []).includes(user.id);
                    return (
                      <motion.button
                        key={user.id}
                        type="button"
                        onClick={() => toggleTeamMember(user.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`
                          flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all
                          ${isSelected
                            ? 'bg-blue-500/20 border-blue-500 dark:bg-blue-500/20'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }
                        `}
                      >
                        <div className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                          ${isSelected
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300 dark:border-gray-600'
                          }
                        `}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {user.name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                              {user.role}
                            </span>
                          </div>
                        </div>
                        {isSelected && (
                          <span className="text-blue-400 text-xl">✓</span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
                {(company.technicianIds || []).length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                      {(company.technicianIds || []).length} team {(company.technicianIds || []).length === 1 ? 'member' : 'members'} selected
                    </p>
                  </div>
                )}
              </div>
            )}
          </Section>
        </div>
      </Card>
    </div>
  );
}

